import * as Sentry from '@sentry/react';
import { AxiosError, isAxiosError, isCancel } from 'axios';

type ReportContext = {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
};

const EXPECTED_ERROR_NAME = 'ExpectedError';

export class ExpectedError extends Error {
  constructor(message?: string) {
    super(message);
    this.name = EXPECTED_ERROR_NAME;
    Object.setPrototypeOf(this, ExpectedError.prototype);
  }
}

export const isExpectedError = (error: unknown): boolean =>
  error instanceof Error && error.name === EXPECTED_ERROR_NAME;

const handledErrors = new WeakSet<object>();

const markHandled = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return true;
  if (handledErrors.has(error)) return false;
  handledErrors.add(error);
  return true;
};

const ID_SEGMENT_PATTERN =
  /^(?:[0-9a-f]{24}|\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d{4}-\d{2}-\d{2})$/i;

export const getRouteTemplate = (url?: string): string => {
  if (!url) return 'unknown';
  const path = url.split(/[?#]/)[0].replace(/^https?:\/\/[^/]+/i, '');
  return path
    .split('/')
    .map((segment) => (ID_SEGMENT_PATTERN.test(segment) ? ':id' : segment))
    .join('/');
};

export const isExpectedHttpError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  if (isCancel(error)) return true;
  const status = error.response?.status;
  if (status === undefined) return true;
  return status < 500;
};

const describeOutcome = (error: AxiosError): string => {
  const status = error.response?.status;
  if (status !== undefined) return String(status);
  if (error.code) return error.code;
  return 'NO_RESPONSE';
};

export const addWarningBreadcrumb = (
  category: string,
  message: string
): void => {
  Sentry.addBreadcrumb({ category, level: 'warning', message });
};

export const reportHttpError = (
  error: AxiosError,
  context?: ReportContext
): void => {
  if (!markHandled(error)) return;

  const method = error.config?.method?.toUpperCase() ?? 'UNKNOWN';
  const route = getRouteTemplate(error.config?.url);
  const outcome = describeOutcome(error);

  if (isExpectedHttpError(error)) {
    addWarningBreadcrumb('api', `${method} ${route} ${outcome}`);
    return;
  }

  Sentry.captureException(error, {
    ...context,
    tags: { ...context?.tags, method, route, status: outcome },
    fingerprint: ['api-error', method, route, outcome],
  });
};

export const reportError = (error: unknown, context?: ReportContext): void => {
  if (isAxiosError(error)) {
    reportHttpError(error, context);
    return;
  }
  if (isExpectedError(error)) return;
  if (!markHandled(error)) return;
  Sentry.captureException(error, context);
};
